import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  type OnInit,
  OnDestroy,
  inject,
  computed,
} from '@angular/core';
import { Router } from '@angular/router';

import { iif, Observable, of, combineLatest, Subscription, filter, map, take, withLatestFrom } from 'rxjs';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { GlobalBookingTravelClass } from '@fcom/core';
import { CampaignTagToLabelKey } from '@fcom/core/constants';
import { AspectRatioString, FinnairThemeService } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  FetchPriorityEnum,
  IconButtonTheme,
  IconPosition,
  TagRoundingStyle,
  TagTheme,
} from '@fcom/ui-components';
import { isPresent } from '@fcom/core/utils';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { Amount } from '@fcom/dapi';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { BlockOffer, MarketingOfferPrices } from '../../interfaces';

@Component({
  selector: 'fin-block-offer-tile',
  templateUrl: './block-offer-tile.component.html',
  styleUrls: ['./block-offer-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BlockOfferTileComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private themeService = inject(FinnairThemeService);

  readonly CmsTripTypeMap = CmsTripTypeMap;
  readonly GlobalBookingTravelClass = GlobalBookingTravelClass;
  readonly MaxImageWidth = 600;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconPosition = IconPosition;
  readonly IconLibrary = IconLibrary;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TagTheme = TagTheme;
  readonly TagRoundingStyle = TagRoundingStyle;

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  @Output()
  linkClicked: EventEmitter<[GlobalBookingTravelClass, TripType]> = new EventEmitter(); // mainly for click tracking if normal link tracking is not enough

  @Output()
  amContinueModalClick = new EventEmitter<{ amLink: string; destinationTitle: string; destinationCode: string }>();

  @Input()
  tripType: TripType = undefined;

  @Input()
  blockOffer$: Observable<BlockOffer>;

  @Input()
  showRoundTripPrice$: Observable<boolean> = of(false);

  @Input()
  fetchImagePriority?: FetchPriorityEnum;

  @Input()
  showPriceCalendarButton = false;

  @Input()
  aspectRatio$: Observable<AspectRatioString> = of('16x9');

  @Output()
  calendarClicked: EventEmitter<[Event, GlobalBookingTravelClass, TripType]> = new EventEmitter();

  innerLinkHover = false;
  flightHotelEnabled = false;
  tripType$: Observable<TripType> = of(undefined);
  badgeLabel$: Observable<string> = of(undefined);
  isHighlightPrice$: Observable<boolean> = of(false);
  priceContext$: Observable<{ price: MarketingOfferPrices }> = of(undefined);
  discountCodeFixedAmount$: Observable<Amount> = of(undefined);

  subscription: Subscription = new Subscription();

  constructor() {
    this.flightHotelEnabled = this.languageService.localeValue.includes('FI');
  }

  ngOnInit(): void {
    this.tripType$ = iif(
      () => isPresent(this.tripType),
      of(this.tripType),
      combineLatest([this.showRoundTripPrice$, this.blockOffer$]).pipe(
        map(([showRoundTripPrice, blockOffer]) =>
          showRoundTripPrice || !blockOffer.isShortHaul ? TripType.RETURN : TripType.ONEWAY
        )
      )
    );

    this.discountCodeFixedAmount$ = this.blockOffer$.pipe(
      filter(({ discountCode }) => discountCode?.type === 'fixed'),
      withLatestFrom(this.languageService.translate('number.currency')),
      map(([{ discountCode }, currencyCode]) => ({
        amount: discountCode.amount,
        currencyCode,
      }))
    );

    const base$ = this.blockOffer$.pipe(filter((offer) => isPresent(offer.badgeLabel)));

    this.badgeLabel$ = base$.pipe(
      map(({ badgeLabel, discountCode }) =>
        badgeLabel === CampaignTagToLabelKey.fcomCampaignCode && isPresent(discountCode?.type)
          ? discountCode.type === 'fixed'
            ? 'destinationSearch.discountCodeFixed'
            : 'destinationSearch.discountCodePercentage'
          : `destinationSearch.${badgeLabel}`
      )
    );

    this.isHighlightPrice$ = base$.pipe(
      map(({ badgeLabel }) =>
        [CampaignTagToLabelKey.fcomCampaignSpecial, CampaignTagToLabelKey.fcomCampaignFlash].includes(
          badgeLabel as CampaignTagToLabelKey
        )
      )
    );

    this.priceContext$ = combineLatest([this.blockOffer$, this.showRoundTripPrice$]).pipe(
      map(([blockOffer, showRoundTripPrices]) => this.getPriceContext(blockOffer, showRoundTripPrices))
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  emitClick(travelClass: GlobalBookingTravelClass, tripType?: TripType): void {
    this.subscription.add(
      this.showRoundTripPrice$.pipe(take(1)).subscribe((showRoundTripPrice) => {
        if (showRoundTripPrice) {
          this.linkClicked.emit([travelClass || GlobalBookingTravelClass.MIXED, TripType.RETURN]);
        } else {
          this.linkClicked.emit([travelClass || GlobalBookingTravelClass.MIXED, tripType]);
        }
      })
    );
  }

  emitCalendarClick(event: Event, travelClass: GlobalBookingTravelClass, tripType?: TripType): void {
    this.calendarClicked.emit([event, travelClass || GlobalBookingTravelClass.MIXED, tripType]);
  }

  openAmContinueModalClick(amLink: string, destinationTitle: string, destinationCode: string): void {
    this.amContinueModalClick.emit({ amLink, destinationTitle, destinationCode });
  }

  navigateToUrl(link: string): void {
    this.router.navigateByUrl(link);
  }

  getPriceContext(
    offer: BlockOffer,
    showRoundTripPrices?: boolean
  ): { price: MarketingOfferPrices; onewayPrice: MarketingOfferPrices; returnPrice: MarketingOfferPrices } {
    if (showRoundTripPrices) {
      return {
        price: offer.returnPrice,
        onewayPrice: offer?.onewayPrice,
        returnPrice: offer?.returnPrice,
      };
    }

    return isPresent(this.tripType)
      ? {
          price: this.tripType === TripType.ONEWAY ? offer.onewayPrice : offer.returnPrice,
          onewayPrice: offer?.onewayPrice,
          returnPrice: offer?.returnPrice,
        }
      : {
          price: offer.isShortHaul ? offer.onewayPrice : offer.returnPrice,
          onewayPrice: offer?.onewayPrice,
          returnPrice: offer?.returnPrice,
        };
  }
}
